import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

const RegisterPage = () => {
    const [fullName, setFullName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    // Check if user is already logged in
    useEffect(() => {
        const userInfo = localStorage.getItem('userInfo');
        if (userInfo) {
            navigate('/'); // Redirect to home if already logged in
        }
    }, [navigate]);

    const submitHandler = async (e) => {
        e.preventDefault();
        setError(''); // Clear previous errors
        setLoading(true);

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ fullName, phoneNumber, email, password }),
            });

            const data = await res.json();

            if (!res.ok) {
                // Check for a specific message from the backend, otherwise use a generic one
                throw new Error(data.message || 'Registration failed. Please try again.');
            }

            // Store user info in local storage and navigate to home
            localStorage.setItem('userInfo', JSON.stringify(data));
            navigate('/'); // Redirect to home page upon successful registration

        } catch (err) {
            // Display a more user-friendly error message
            setError((err instanceof Error ? err.message : String(err)) || 'Something went wrong. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4 sm:p-6 lg:p-8 font-inter">
            <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 lg:p-10 w-full max-w-md border border-gray-100 transform transition-all duration-300 hover:scale-[1.01]">
                <h2 className="text-3xl font-extrabold text-center mb-7 text-gray-900 drop-shadow-sm">
                    Create Your Account
                </h2>

                {/* Error Message Display */}
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative mb-5 shadow-md animate-fade-in-down" role="alert">
                        <strong className="font-bold">Registration Failed:</strong>
                        <span className="block sm:inline ml-2">{error}</span>
                    </div>
                )}

                <form onSubmit={submitHandler} className="space-y-6">
                    {/* Full Name Input */}
                    <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="fullName">
                            Full Name:
                        </label>
                        <input
                            type="text"
                            id="fullName"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out disabled:bg-gray-100 disabled:text-gray-500"
                            placeholder="Enter your full name"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            required
                            disabled={loading}
                        />
                    </div>
                    {/* Phone Number Input */}
                    <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="phoneNumber">
                            Phone Number:
                        </label>
                        <input
                            type="tel"
                            id="phoneNumber"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out disabled:bg-gray-100 disabled:text-gray-500"
                            placeholder="e.g., 07XXXXXXXX"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            required
                            disabled={loading}
                        />
                    </div>
                    {/* Email Input */}
                    <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="email">
                            Email :
                        </label>
                        <input
                            type="email"
                            id="email"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out disabled:bg-gray-100 disabled:text-gray-500"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            disabled={loading}
                        />
                    </div>
                    {/* Password Input */}
                    <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="password">
                            Password:
                        </label>
                        <input
                            type="password"
                            id="password"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out disabled:bg-gray-100 disabled:text-gray-500"
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength="6"
                            disabled={loading}
                        />
                    </div>
                    {/* Confirm Password Input */}
                    <div>
                        <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="confirmPassword">
                            Confirm Password:
                        </label>
                        <input
                            type="password"
                            id="confirmPassword"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200 ease-in-out disabled:bg-gray-100 disabled:text-gray-500"
                            placeholder="••••••••"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            minLength="6"
                            disabled={loading}
                        />
                    </div>
                    {/* Register Button */}
                    <div className="pt-4">
                        <button
                            type="submit"
                            className={`
                                py-3 px-6 rounded-lg focus:outline-none focus:ring-4 w-full text-xl transition duration-300 transform hover:scale-105
                                ${loading
                                    ? 'bg-gray-500 text-white cursor-not-allowed shadow-none'
                                    : 'bg-gradient-to-r from-green-600 to-green-800 hover:from-green-700 hover:to-green-900 text-white font-bold shadow-lg focus:ring-green-300'
                                }
                            `}
                            disabled={loading}
                        >
                            {loading ? 'Registering...' : 'Register'}
                        </button>
                    </div>
                </form>
                {/* Login Link */}
                <p className="text-center mt-6 text-gray-600 text-md">
                    Already have an account?{' '}
                    <Link to="/login" className="text-blue-600 hover:underline font-bold text-lg hover:text-blue-800 transition duration-300">
                        Login here!
                    </Link>
                </p>
            </div>
        </div>
    );
};

export default RegisterPage;
